import { useTranslation } from "react-i18next"
import { Formik, Form, Field } from "formik"
import _ from 'lodash'

import { useNavigate, useParams, Link } from "../navigation";
import Modal from "../components/Modal"
import { DatePicker } from "../components/input/DatePicker"
import { useDesigns } from "./DesignsProvider"
import { DesignFamily } from "./designs"
import { designFamilyUrl } from "./utils"

export default function PostDesignFamily() {
    const { t } = useTranslation()
    const { reference } = useParams()
    const navigate = useNavigate()
    const { families, familyByReference, postFamily } = useDesigns()

    const family = familyByReference[reference]
    const isAdding = !family

    const otherFamilyReferences = new Set(_(families).filter(f => f.designFamilyId !== family?.designFamilyId).map(f => f.reference).value())

    const initialValues = family ?? {
        reference: '',
        externalReference: '',
        name: '',
        description: '',
        priorityDate: '',
        collectiveFiling: false,
    }

    return (
        <Modal>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values: DesignFamily) => {
                    // Convert empty string dates to undefined for backend compatibility
                    const cleanedValues = {
                        ...values,
                        priorityDate: values.priorityDate || undefined,
                    }
                    const result = await postFamily(cleanedValues)
                    navigate(designFamilyUrl(result))
                }}
                validate={(values: DesignFamily) => {
                    const errors: Record<string, string> = {}
                    if (!values.reference) {
                        errors.reference = t('required')
                    } else if (otherFamilyReferences.has(values.reference)) {
                        errors.reference = t('reference-used')
                    }
                    if (!values.name) {
                        errors.name = t('required')
                    }
                    return errors
                }}
            >{({ touched, errors }) =>
                <Form>
                    <div className="p-4 flex flex-col gap-4">
                        <h2>{isAdding ? t('add-design-family') : t('edit-design-family', { name: initialValues.reference })}</h2>
                        <label className="w-full">
                            <div className="label">{t('reference')}</div>
                            <Field name="reference" className="form-input w-full mt-1" type="text"
                                required autoFocus pattern="[a-zA-Z0-9 ._\-]*" title={t('use-usernames')}
                            />
                            {touched.reference && errors.reference && <div className="text-red-500 mt-1">{errors.reference}</div>}
                        </label>
                        <label className="w-full">
                            <div className="label">{t('name')}</div>
                            <Field name="name" className="form-input w-full mt-1" type="text" required />
                            {touched.name && errors.name && <div className="text-red-500 mt-1">{errors.name}</div>}
                        </label>
                        <label className="w-full">
                            <div className="label">{t('externalReference')}</div>
                            <Field name="externalReference" className="form-input w-full mt-1" type="text" />
                        </label>
                        <label className="w-full">
                            <div className="label">{t('description')}</div>
                            <Field name="description" className="form-textarea mt-1 w-full sm:min-h-[10rem]" as="textarea" />
                        </label>
                        <label className="w-full">
                            <div className="label">{t('priorityDate')}</div>
                            <Field name="priorityDate" className="form-input w-full mt-1 py-2" as={DatePicker} />
                        </label>
                    </div>
                    <div className="bg-pcx-200 flex flex-row-reverse gap-4 p-4">
                        <input type="submit" className="btn-primary" value={t('save')} />
                        {/* @ts-expect-error - React Router navigate(-1) type issue */}
                        <Link to={-1} className="btn-secondary">{t('cancel')}</Link>
                    </div>
                </Form>
                }</Formik>
        </Modal>
    )
}
